<template>
  <div class="page">
    <div class="heading"><div><h1>存档管理</h1><p>查看农场存档、创建和下载备份，必要时安全恢复。</p></div><el-button :loading="loading" @click="refresh">刷新</el-button></div>
    <el-alert :closable="false" type="warning" show-icon title="恢复存档会覆盖当前游戏进度" description="恢复前必须停服，系统会额外保存一份恢复前快照；不要在玩家在线时恢复。导入其他玩家的存档后，还需确认角色归属。" />
    <div class="panel">
      <div class="section"><h3>服务器上的农场</h3><span class="hint">共 {{saves.length}} 个农场</span></div>
      <el-table :data="saves" empty-text="暂时没有可识别的农场存档" style="width:100%;margin-top:15px">
        <el-table-column label="农场" min-width="210"><template #default="{row}"><strong>{{friendlySave(row.name)}}</strong></template></el-table-column>
        <el-table-column label="存档状态" width="130"><template #default>已保存</template></el-table-column>
      </el-table>
      <div class="hint">农场名称来自已保存的游戏文件。导入新农场不会自动替换正在使用的世界。</div>
    </div>
    <div class="panel">
      <div class="section"><h3>手动存档备份</h3><el-button type="primary" :loading="backupBusy" @click="backupNow">立即备份</el-button></div>
      <p class="hint">备份和清理时间可在「自动备份」里设置。备份时会先确认没有玩家在线，避免影响游戏进度。</p>
      <el-table :data="backups" empty-text="暂无备份" style="width:100%;margin-top:16px">
        <el-table-column label="备份内容" min-width="115"><template #default>农场完整备份</template></el-table-column>
        <el-table-column label="生成时间" min-width="165"><template #default="{row}">{{new Date(row.createdAt).toLocaleString('zh-CN',{hour12:false})}}</template></el-table-column>
        <el-table-column label="大小" min-width="95"><template #default="{row}">{{(row.bytes/1024).toFixed(1)}} KB</template></el-table-column>
        <el-table-column label="操作" min-width="200"><template #default="{row}">
          <el-button size="small" @click="download(row)">下载</el-button>
          <el-button size="small" type="danger" plain :loading="restoring===row.name" @click="restore(row)">恢复</el-button>
        </template></el-table-column>
      </el-table>
    </div>
    <div class="panel">
      <h3>从电脑导入存档</h3>
      <p class="hint">将电脑中的整个农场存档文件夹压缩成 ZIP 文件后上传。导入后只会新增存档，不会自动替换当前农场。</p>
      <el-upload ref="uploader" drag accept=".zip" :auto-upload="false" :limit="1" :on-change="f=>selected=f.raw" :on-exceed="()=>ElMessage.warning('一次选择一个 ZIP')">
        <div class="upload-box"><strong>选择存档 ZIP</strong><small>请将电脑里的完整农场文件夹压缩，而不是只压缩单个存档文件</small></div>
      </el-upload>
      <el-button type="primary" plain :loading="importing" :disabled="!selected" style="margin-top:12px" @click="importSave">导入服务器存档目录</el-button>
    </div>
  </div>
</template>
<script setup>
import {ref,onMounted} from 'vue'
import {ElMessage,ElMessageBox} from 'element-plus'
import {http} from '../plugins/axios'
import {authErrorMessage} from '../auth'
function friendlySave(value){return String(value||'未命名农场').replace(/_[0-9]{3,20}$/,'')||'未命名农场'}
const loading=ref(false),backupBusy=ref(false),restoring=ref(''),backups=ref([]),saves=ref([]),uploader=ref(null),selected=ref(null),importing=ref(false)
async function refresh(){
 loading.value=true
 try{
  const [a,b]=await Promise.all([http.get('/api/manage/admin/backups'),http.get('/api/manage/admin/saves')])
  backups.value=a.data;saves.value=b.data
 }catch(e){ElMessage.error(authErrorMessage(e,'读取存档失败'))}
 finally{loading.value=false}
}
async function backupNow(){
 backupBusy.value=true
 try{ElMessage.success((await http.post('/api/manage/backup/run')).data.message);ElMessage.info('备份正在后台执行，稍后点击刷新查看')}
 catch(e){ElMessage.error(authErrorMessage(e,'备份失败'))}
 finally{backupBusy.value=false}
}
async function restore(row){
 try {
  await ElMessageBox.confirm('确认恢复「'+row.name+'」吗？当前游戏必须已停止，恢复前会自动保存现有存档快照。','高风险操作',{type:'warning',confirmButtonText:'继续确认',cancelButtonText:'取消'})
  const {value}=await ElMessageBox.prompt('请输入“恢复存档”确认操作。此操作会替换当前农场进度','最终确认',{confirmButtonText:'确认恢复',cancelButtonText:'取消',inputPattern:/^恢复存档$/,inputErrorMessage:'请输入：恢复存档'})
  if(value!=='恢复存档')return
 }catch(_){return}
 restoring.value=row.name
 try{const r=await http.post('/api/manage/admin/backups/restore',{name:row.name,confirm:'恢复存档'},{timeout:120000});ElMessage.success(r.data.message);await refresh()}
 catch(e){ElMessage.error(authErrorMessage(e,'恢复失败'))}
 finally{restoring.value=''}
}
async function download(row){
 try{
  const response=await http.get('/api/manage/admin/backups/download/'+encodeURIComponent(row.name),{responseType:'blob',timeout:120000})
  const url=URL.createObjectURL(response.data)
  const link=document.createElement('a');link.href=url;link.download=row.name
  document.body.appendChild(link);link.click();link.remove()
  URL.revokeObjectURL(url)
 }catch(e){ElMessage.error(authErrorMessage(e,'下载失败'))}
}
async function importSave(){
 if(!selected.value)return
 try{await ElMessageBox.confirm('确认游戏已停止？这只会把 ZIP 导入存档目录，不会自动切换或覆盖现有农场。','确认导入',{type:'warning'})}catch(_){return}
 importing.value=true
 try{
  const form=new FormData();form.append('file',selected.value)
  const r=await http.post('/api/manage/admin/saves/import',form,{timeout:120000})
  ElMessage.success(r.data.message);uploader.value?.clearFiles();selected.value=null;await refresh()
 }catch(e){ElMessage.error(authErrorMessage(e,'导入失败'))}
 finally{importing.value=false}
}
onMounted(refresh)
</script>
<style scoped>
.page{max-width:1150px}.heading,.section{display:flex;justify-content:space-between;align-items:center;gap:15px}.heading{margin-bottom:22px}h1{margin:0;font-size:27px}h3{margin:0;font-size:17px}.heading p,.hint{font-size:13px;line-height:1.8;color:#708175}.panel{margin-top:19px;padding:25px;background:#fff;border:1px solid #e5eee6;border-radius:14px}.panel .hint{margin:14px 0}.upload-box{display:flex;flex-direction:column;gap:10px}.upload-box small{font-size:12px;color:#84968a}@media(max-width:650px){.panel{padding:16px}.heading,.section{flex-wrap:wrap}}
</style>
